// 引入 React 与常用 Hook。
import React, { useState, useEffect, useRef, useContext } from 'react';
// 引入国际化 Hook。
import { useTranslations } from 'next-intl';
// 引入 className 合并工具。
import cx from 'classnames';
// 引入通用图标组件。
import CommonIcon from '@/components/CommonIcon';
// 引入通用图片组件。
import CommonImage, { ImageType } from '@/components/CommonImage';
// 引入 Toast 组件。
import Toast from '@/components/Toast';
// 引入全局上下文。
import { StoreContext, namespace } from '@/context';
// 引入 SLS 上报能力。
import useSls, { MetricTypes } from '@/hooks/useSls';
// 引入接口错误码。
import { ErrorCodes } from '@/utils/fetch';
import { 
  getRecommendAvatarNameList, 
  uploadAvatarService, 
  usersUpdateService, 
  IAvatar, 
  INickName 
} from '@/api/profile';
import { IUserInfo, IUserLoginInfo } from '@/api/user';
import Styles from './index.module.scss';
import DefaultAvatar from '@/assets/icon/account/def_avatar.webp';
import SelectAvatarOkIcon from '../../../../assets/icon/account/select-avatar-ok.svg';
import LoadingIcon from '../../../../assets/icon/account/loading-five.svg';

// 引入头像裁剪弹层组件。
import AvatarCropper from '../AvatarCropper';

// 编辑资料弹层入参。
interface EditProfileModalProps {
  visible: boolean;
  onClose: () => void;
  userInfo: IUserInfo | null;
  account: IUserLoginInfo | null;
  onSuccess: (payload?: { name?: string; avatarUrl?: string }) => void | Promise<void>;
}

// 昵称校验正则与边界常量。
const NICKNAME_REGEX = /^[\u4e00-\u9fa5a-zA-Z0-9_-]{2,15}$/;
const NICKNAME_CHAR_REGEX = /^[\u4e00-\u9fa5a-zA-Z0-9_-]*$/;
const MIN_NICKNAME_LENGTH = 2;
const MAX_NICKNAME_LENGTH = 15;
const RECOMMEND_NICKNAME_LIMIT = 4;
const nicknameErrorCodes = {
  lengthMin: 'length_min',
  lengthMax: 'length_max',
  specialCharacter: 'special_character',
  beOccupied: 'be_occupied',
  notRegulation: 'not_regulation',
} as const;
type NicknameErrorCode = typeof nicknameErrorCodes[keyof typeof nicknameErrorCodes];

// 编辑资料弹层组件。
const EditProfileModal: React.FC<EditProfileModalProps> = ({
  visible,
  onClose,
  userInfo,
  account,
  onSuccess,
}) => {
  // 国际化函数。
  const t = useTranslations();
  // 读取全局 sensors。
  const { state } = useContext(StoreContext);
  const { sensors } = state[namespace.app];
  const { track, TRACK_EVENTS } = sensors || {};
  const { reportSlsData } = useSls();
  
  // State
  // 推荐数据加载态。
  const [loading, setLoading] = useState(false);
  // 提交按钮加载态。
  const [submitLoading, setSubmitLoading] = useState(false);
  
  // Cropper State
  const [showCropper, setShowCropper] = useState(false);
  const [cropperFile, setCropperFile] = useState<File | null>(null);

  // Data
  const [avatarList, setAvatarList] = useState<IAvatar[]>([]);
  const [nicknameList, setNicknameList] = useState<INickName[]>([]);
  const [maxNicknameId, setMaxNicknameId] = useState<number>(0);
  
  // Selection
  const [activeAvatarId, setActiveAvatarId] = useState<number | string>(''); // ID or 'custom'
  const [customAvatarUrl, setCustomAvatarUrl] = useState<string>('');
  const [customAvatarAttachment, setCustomAvatarAttachment] = useState<string>('');
  
  const [nickname, setNickname] = useState('');
  const [nicknameError, setNicknameError] = useState<NicknameErrorCode>();
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  // 防止弹层可见期间重复初始化。
  const hasInitializedRef = useRef(false);
  
  // Drag to scroll logic
  const avatarListRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const isDragClick = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const onMouseDown = (e: React.MouseEvent) => {
    if (!avatarListRef.current) return;
    isDragging.current = true;
    isDragClick.current = false;
    startX.current = e.pageX - avatarListRef.current.offsetLeft;
    scrollLeft.current = avatarListRef.current.scrollLeft;
  };

  const onMouseLeave = () => {
    isDragging.current = false;
  };

  const onMouseUp = () => {
    isDragging.current = false;
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !avatarListRef.current) return;
    e.preventDefault();
    const x = e.pageX - avatarListRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5; // Scroll speed multiplier
    avatarListRef.current.scrollLeft = scrollLeft.current - walk;
    if (Math.abs(x - startX.current) > 5) {
      isDragClick.current = true;
    }
  };

  // Initial Data Load
  useEffect(() => {
    if (!visible) {
      hasInitializedRef.current = false;
      setNicknameError(undefined);
      return;
    }
    if (hasInitializedRef.current) {
      return;
    }

    hasInitializedRef.current = true;
    const currentAvatar = account?.avatar?.large || userInfo?.avatar || '';
    const currentName = account?.name || userInfo?.name || '';

    setCustomAvatarUrl(currentAvatar);
    setCustomAvatarAttachment('');
    setNickname(currentName);
    setNicknameError(undefined);
    setActiveAvatarId('custom');
    fetchRecommendations();
  }, [visible, userInfo, account]);

  // 弹层展示埋点。
  useEffect(() => {
  // 首次打开弹层时初始化数据与推荐列表。
    if (!visible) return;
    if (!visible) return;
    track?.(TRACK_EVENTS?.[namespace.profile]?.popupShow?.name, {
      page_name: 'MyProfilePage',
      platform_type: 'pc',
      pop_slug: 'EditProfile',
    }, namespace.profile);
  }, [visible, track, TRACK_EVENTS]);

  // 拉取推荐头像与昵称。
  const fetchRecommendations = async (startId?: number) => {
    setLoading(true);
    try {
      const res = await getRecommendAvatarNameList({
        startId: startId || 0,
        size: 8
      });

      setAvatarList(res.avatar_list || []);
      setNicknameList((res.nick_name_list || []).slice(0, RECOMMEND_NICKNAME_LIMIT));
      setMaxNicknameId(res.max_nick_name_id);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
      Toast.show({ content: t('user_center_edit_profile_fetch_recommend_failed'), isPc: true });
    } finally {
      setLoading(false);
    }
  };

  // 刷新推荐昵称。
  const handleRefreshNicknames = () => {
    fetchRecommendations(maxNicknameId);
    track?.(TRACK_EVENTS?.[namespace.profile]?.randomizeNicknameClick?.name, {
      page_name: 'MyProfilePage',
      platform_type: 'pc',
      pop_slug: 'EditProfile',
    }, namespace.profile);
  };

  // 选择推荐头像。
  const handleAvatarSelect = (item: IAvatar) => {
    if (isDragClick.current) return;
    setActiveAvatarId(item.id);
    setCustomAvatarUrl(item.image_url.large);
    setCustomAvatarAttachment(item.attachment);
    track?.(TRACK_EVENTS?.[namespace.profile]?.defaultAvatarClick?.name, {
      page_name: 'MyProfilePage',
      platform_type: 'pc',
      pop_slug: 'EditProfile',
      avatar_slug: item.id,
    }, namespace.profile);
  };

  // 选择推荐昵称。
  const handleNicknameSelect = (name: string) => {
    setNickname(name);
    setNicknameError(undefined);
    track?.(TRACK_EVENTS?.[namespace.profile]?.defaultNicknameClick?.name, {
      page_name: 'MyProfilePage',
      platform_type: 'pc',
      pop_slug: 'EditProfile',
      nickname_slug: name,
    }, namespace.profile);
  };

  // 处理本地文件选择。
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) {
      input.value = '';
      return;
    }

    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      Toast.show({ content: t('user_center_edit_profile_avatar_format_error'), isPc: true });
      input.value = '';
      return;
    }
    
    if (file.size > 3 * 1024 * 1024) {
      Toast.show({ content: t('user_center_edit_profile_avatar_size_error'), isPc: true });
      input.value = '';
      return;
    }

    setCropperFile(file);
    setShowCropper(true);
    input.value = '';
  };

  // 关闭裁剪弹层。
  const handleCropperClose = () => {
    setShowCropper(false);
    setCropperFile(null);
  };

  // 上报头像上传埋点结果。
  const reportUploadAvatarResult = (isSuccess: boolean, failReason: string) => {
    track?.(TRACK_EVENTS?.[namespace.profile]?.uploadAvatarResult?.name, {
      page_name: 'MyProfilePage',
      platform_type: 'pc',
      pop_slug: 'EditProfile',
      is_success: isSuccess,
      fail_reason: failReason,
    }, namespace.profile);
  };

  // 上报编辑弹层提交结果。
  const reportPopUpClickResult = (isSuccess: boolean, failReason: string) => {
    track?.(TRACK_EVENTS?.[namespace.profile]?.popUpClick?.name, {
      page_name: 'MyProfilePage',
      platform_type: 'pc',
      pop_slug: 'EditProfile',
      is_success: isSuccess,
      fail_reason: failReason,
    }, namespace.profile);
  };

  // 裁剪保存后上传头像并回填预览。
  const handleCropperSave = async (file: File) => {
    const requestStartTime = Date.now();
    const paramsPayload = {
      file_name: file.name || '',
      file_size: file.size || 0,
      file_type: file.type || '',
    };
    const reportAvatarUploadSls = (failReason: string, responseCode: string | number) => {
      const duration = Date.now() - requestStartTime;
      const uid = Number(account?.id);
      reportSlsData([
        ['reportMetric', MetricTypes.userAvatarUploadApiTime, duration],
        [
          'setEnvironment',
          {
            api_path: '/forum/files/user-head/upload',
            api_params: JSON.stringify(paramsPayload),
            uid: Number.isFinite(uid) ? uid : null,
            is_success: Number(responseCode) === 200 ? 1 : 0,
            fail_reason: failReason,
            terminal_type: 'pc',
            response_code: responseCode,
          },
        ],
      ]);
    };
    try {
      Toast.show({ 
        content: t('user_center_edit_profile_avatar_uploading'),
        renderIcon: <LoadingIcon className={Styles.toastLoadingIcon} />, 
        duration: 0, 
        isPc: true 
      });
      const res = await uploadAvatarService({ file });
      Toast.clear();
      const responseCode = (res as any)?.code ?? (res as any)?.status ?? 200;
      
      if (res && res.url) {
        reportAvatarUploadSls('', responseCode);
        // Use local blob URL for immediate preview to avoid CDN/OSS propagation delays
        // and potential WebP conversion issues on the new resource.
        const blobUrl = URL.createObjectURL(file);
        setCustomAvatarUrl(blobUrl);
        setCustomAvatarAttachment(res.key); // Assuming 'key' corresponds to attachment ID
        setActiveAvatarId('custom');
        handleCropperClose();
        Toast.show({ content: t('user_center_edit_profile_avatar_upload_success'),  isPc: true });
        reportUploadAvatarResult(true, '');
      } else {
        reportAvatarUploadSls(t('user_center_edit_profile_avatar_upload_result_error'), responseCode);
        reportUploadAvatarResult(false, t('user_center_edit_profile_avatar_upload_result_error'));
      }
    } catch (error: any) {
      Toast.clear();
      console.error('Upload failed:', error);
      const failReason = error?.message || t('user_center_edit_profile_avatar_upload_failed');
      const responseCode = error?.code ?? error?.status ?? error?.response?.status ?? -1;
      reportAvatarUploadSls(failReason, responseCode);
      reportUploadAvatarResult(false, failReason);
      if (error?.code === 10031) {
        Toast.show({ content: error?.message || t('user_center_edit_profile_update_limit_reached'), isPc: true });
      } else {
        Toast.show({ content: t('user_center_edit_profile_avatar_upload_failed'), isPc: true });
      }
      throw error; 
    }
  };

  // 昵称校验。
  const validateNickname = (nextNickname: string) => {
    const trimmed = nextNickname.trim();
    if (nextNickname && !trimmed) {
      setNicknameError(nicknameErrorCodes.specialCharacter);
      return;
    }
    if (!trimmed) {
      setNicknameError(undefined);
      return;
    }
    if (!NICKNAME_CHAR_REGEX.test(nextNickname)) {
      setNicknameError(nicknameErrorCodes.specialCharacter);
      return;
    }
    if (trimmed.length < MIN_NICKNAME_LENGTH) {
      setNicknameError(nicknameErrorCodes.lengthMin);
      return;
    }
    if (trimmed.length > MAX_NICKNAME_LENGTH) {
      setNicknameError(nicknameErrorCodes.lengthMax);
      return;
    }
    setNicknameError(undefined);
  };

  // 输入时实时校验昵称。
  const handleNicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextNickname = e.target.value;
    setNickname(nextNickname);
    validateNickname(nextNickname);
  };

  // 失焦时再次校验昵称。
  const handleNicknameBlur = () => {
    validateNickname(nickname);
  };

  // 渲染昵称错误提示。
  const renderNicknameTip = () => {
    switch (nicknameError) {
      case nicknameErrorCodes.lengthMax:
        return <p className={Styles.nicknameTip}>{t('profile_nickname_max_length')}</p>;
      case nicknameErrorCodes.beOccupied:
        return <p className={Styles.nicknameTip}>{t('profile_nickname_be_occupied')}</p>;
      case nicknameErrorCodes.notRegulation:
        return <p className={Styles.nicknameTip}>{t('profile_nickname_not_regulation')}</p>;
      case nicknameErrorCodes.lengthMin:
      case nicknameErrorCodes.specialCharacter:
        return <p className={Styles.nicknameTip}>{t('profile_nickname_rule_tip')}</p>;
      default:
        return null;
    }
  };

  const trimmedNickname = nickname.trim();
  // 提交按钮禁用态。
  const disableSubmit = submitLoading || !trimmedNickname || !!nicknameError;

  // 提交资料更新。
  const handleSubmit = async () => {
    if (disableSubmit) {
      return;
    }
    if (!NICKNAME_REGEX.test(trimmedNickname)) {
      setNicknameError(nicknameErrorCodes.specialCharacter);
      return;
    }

    setSubmitLoading(true);
    try {
      // If activeAvatarId is 'custom', we use customAvatarAttachment.
      // If it's a recommended one, we use its attachment.
      let attachment = customAvatarAttachment;
      
      // If user hasn't changed avatar (still using initial), attachment might be empty if we didn't store it.
      // However, the API requires 'attachment' field.
      // If we don't have a new attachment key, we might be sending empty string which means "no change" or "remove"?
      // Based on mobile code:
      // usersUpdateService({
      //   attachment: currentAvatar?.attachment || '',
      //   nick_name: inputVal?.trim() || '',
      //   operation_name_id: ...
      // })
      
      // If the user selected a recommended avatar, use that attachment.
      if (typeof activeAvatarId === 'number') {
         const selected = avatarList.find(a => a.id === activeAvatarId);
         if (selected) {
           attachment = selected.attachment;
         }
      }

      // Check if recommended nickname selected
      const selectedNicknameObj = nicknameList.find(n => n.name === trimmedNickname);
      const operationNameId = selectedNicknameObj ? selectedNicknameObj.id : null;

      const updatedUser = await usersUpdateService({
        attachment: attachment || '', // If empty, backend likely keeps current or handles it.
        nick_name: trimmedNickname,
        operation_name_id: operationNameId
      });

      reportPopUpClickResult(true, '');
      Toast.show({ content: t('user_center_edit_profile_update_success'),  isPc: true });
      await onSuccess({
        name: updatedUser?.name || trimmedNickname,
        avatarUrl: updatedUser?.imageUrl?.large || customAvatarUrl || undefined,
      });
      onClose();
    } catch (error: any) {
      console.error('Update failed:', error);
      const msg = error?.message || t('user_center_edit_profile_update_failed');
      reportPopUpClickResult(false, msg);
      if (error?.code === ErrorCodes.beOccupied) {
        setNicknameError(nicknameErrorCodes.beOccupied);
      } else if (error?.code === ErrorCodes.nickNotRegulation) {
        setNicknameError(nicknameErrorCodes.notRegulation);
      } else {
        Toast.show({ content: msg, isPc: true });
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  // 关闭弹层并上报关闭埋点。
  const handleClose = () => {
    track?.(TRACK_EVENTS?.[namespace.profile]?.popUpCloseClick?.name, {
      page_name: 'MyProfilePage',
      platform_type: 'pc',
      pop_slug: 'EditProfile',
    }, namespace.profile);
    onClose();
  };

  // 不可见时不渲染。
  if (!visible) return null;

  return (
    <>
      {!showCropper && (
        <div className={Styles.mask}>
          <div className={Styles.container}>
            <div className={Styles.closeBtn} onClick={handleClose}>
              <CommonIcon icon="my-info-close" />
            </div>

            <div className={Styles.header}>
              <div className={Styles.title}>{t('user_center_edit_profile_title')}</div>
              <div className={Styles.subtitle}>{t('user_center_edit_profile_subtitle')}</div>
            </div>

            <div className={Styles.avatarSection}>
              <div className={Styles.preview}>
                <CommonImage 
                  src={customAvatarUrl || DefaultAvatar} 
                  alt="avatar" 
                  type={ImageType.avatar}
                  layout="fill"
                  cutMode="cover"
                />
              </div>
              
              <div className={Styles.selectionRow}>
                <div
                  className={Styles.uploadBtn}
                  onClick={() => {
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                      fileInputRef.current.click();
                    }
                  }}
                >
                  <input 
                    type="file" 
                    ref={fileInputRef} 
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }} 
                  />
                  <CommonIcon icon="camera-fill" className={Styles.cameraIcon} />
                </div>
                
                <div className={Styles.divider}></div>

                <div 
                  className={Styles.avatarList}
                  ref={avatarListRef}
                  onMouseDown={onMouseDown}
                  onMouseLeave={onMouseLeave}
                  onMouseUp={onMouseUp}
                  onMouseMove={onMouseMove}
                >
                  {avatarList.map(item => (
                    <div 
                      key={item.id} 
                      className={cx(Styles.avatarItem, { [Styles.active]: activeAvatarId === item.id })}
                      onClick={() => handleAvatarSelect(item)}
                    >
                      <CommonImage 
                        src={item.image_url.medium} 
                        alt={item.name} 
                        type={ImageType.avatar}
                        layout="fill"
                        cutMode="cover"
                        draggable={false}
                      />
                      {activeAvatarId === item.id && (
                        <div className={Styles.checkIcon}>
                          <SelectAvatarOkIcon />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={Styles.nicknameSection}>
              <div className={Styles.inputWrapper}>
                <input 
                  value={nickname}
                  onChange={handleNicknameChange}
                  onBlur={handleNicknameBlur}
                  placeholder={t('profile_input_nickname')}
                />
                {nickname && (
                  <div className={Styles.clearBtn} onClick={() => {
                    setNickname('');
                    setNicknameError(undefined);
                  }}>
                    <CommonIcon icon="search-close-circle" />
                  </div>
                )}
              </div>
              {renderNicknameTip()}

              <div className={Styles.recommendHeader}>
                <span className={Styles.label}>{t('user_center_edit_profile_recommend_nickname')}</span>
                <div className={Styles.refreshBtn} onClick={handleRefreshNicknames}>
                  <CommonIcon icon="star" className={Styles.refreshIcon} />
                  {t('user_center_edit_profile_refresh_nickname')}
                </div>
              </div>

              <div className={Styles.recommendList}>
                {nicknameList.map(item => (
                  <div 
                    key={item.id} 
                    className={cx(Styles.tag, { [Styles.active]: nickname === item.name })}
                    onClick={() => handleNicknameSelect(item.name)}
                  >
                    {item.name}
                  </div>
                ))}
              </div>
            </div>

            <div className={Styles.footer}>
              <button 
                className={Styles.submitBtn} 
                onClick={handleSubmit}
                disabled={disableSubmit}
              >
                {submitLoading && <CommonIcon icon="loading-white" className={Styles.loadingIcon} />}
                {t('user_center_edit_profile_done')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <AvatarCropper 
        visible={showCropper} 
        imageFile={cropperFile} 
        onClose={handleCropperClose} 
        onSave={handleCropperSave} 
      />
    </>
  );
};

// 导出组件。
export default EditProfileModal;
